import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Scissors, CheckCircle2, XCircle, Loader2 } from "lucide-react";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function VerifyEmailPage() {
  const q = useQuery();
  const token = q.get("token") || "";
  const [state, setState] = useState(token ? "loading" : "no-token"); // loading|success|error|no-token
  const [msg, setMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!token) return;
    (async () => {
      try {
        await api.post("/auth/verify-email/confirm", { token });
        if (!cancelled) { setState("success"); }
      } catch (e) {
        if (!cancelled) {
          setState("error");
          setMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#FAFAFA] text-[#0A0A0A]" data-testid="verify-email-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <Scissors size={20} className="text-[#0A0A0A]" strokeWidth={1.5}/>
          <div className="font-serif text-xl tracking-widest">MMS HUB CENTER</div>
        </div>
        <div className="border border-[#E5E5E5] p-8 bg-[#FFFFFF]">
          {state === "loading" && (
            <div className="flex flex-col items-center text-center" data-testid="verify-email-loading">
              <Loader2 size={28} className="text-[#0A0A0A] animate-spin mb-4" strokeWidth={1.5}/>
              <div className="label-caps text-[#0A0A0A] mb-3">Verifying</div>
              <p className="text-sm text-[#525252]">Confirming your email…</p>
            </div>
          )}

          {state === "success" && (
            <div className="text-center" data-testid="verify-email-success">
              <CheckCircle2 size={28} className="text-green-400 mb-4 mx-auto" strokeWidth={1.5}/>
              <div className="label-caps text-[#0A0A0A] mb-3">Verified</div>
              <h2 className="font-serif text-3xl font-light mb-3">Email confirmed</h2>
              <p className="text-sm text-[#525252] leading-relaxed mb-6">
                Your account is now active. You can sign in.
              </p>
              <Link to="/login" data-testid="verify-email-login-btn" className="btn-gold block text-center">
                Sign in
              </Link>
            </div>
          )}

          {state === "error" && (
            <div className="text-center" data-testid="verify-email-error">
              <XCircle size={28} className="text-red-600 mb-4 mx-auto" strokeWidth={1.5}/>
              <div className="label-caps text-red-300 mb-3">Failed</div>
              <h2 className="font-serif text-3xl font-light mb-3">Verification failed</h2>
              <p className="text-sm text-[#525252] leading-relaxed mb-6" data-testid="verify-email-error-msg">
                {msg || "The link is invalid or has expired."}
              </p>
              <Link to="/login" className="text-xs text-[#0A0A0A] hover:text-[#171717] uppercase tracking-widest">Back to sign in</Link>
            </div>
          )}

          {state === "no-token" && (
            <div className="text-center" data-testid="verify-email-no-token">
              <XCircle size={28} className="text-red-600 mb-4 mx-auto" strokeWidth={1.5}/>
              <h2 className="font-serif text-3xl font-light mb-3">Missing token</h2>
              <p className="text-sm text-[#525252] leading-relaxed mb-6">This URL is incomplete.</p>
              <Link to="/login" className="text-xs text-[#0A0A0A] hover:text-[#171717] uppercase tracking-widest">Back to sign in</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
